// 261010 P1a (luồng Sky) — logic THUẦN cho chip engine + khối "Giai đoạn" ở màn Event. KHÔNG import antd: dưới jest của react-scripts
// antd v6 + react-router-dom v7 không load được ⇒ logic phải tách khỏi file component (mẫu `stagingStatusUtils.ts`).
// Chỉ ĐỌC ở P1a: gen vẫn theo 2.0 (snap ≤15s) tới P1b.
import type { FilmActionBeat, FilmEvent, FilmFlowVersion, FilmVideoEngine, FilmVideoMeta, FilmVideoPhase, GenerationBody, GenTuning } from '../../types/film';
import { generationBodyOf, optInTuningOf, type GenerationForm } from './workflowGenerationUtils';

export const SKY_GEN_NOTE = 'Gen theo engine này. Đổi được ở đây (2.5→2.0 chỉ khi ước ≤15s).';
export const SKY_TOGGLE_TOOLTIP = 'Chia theo tình huống kịch, có đề xuất engine.';
/** P7c — công tắc "Luồng mới" ở form "New Film Plan" mặc định BẬT (User 26/10); tắt tay ⇒ plan chia như luồng cũ. */
export const SKY_FLOW_DEFAULT = true;

/**
 * Băm nhịp — BẢN SAO của BE `computeBeatsHash` (`admin-service/src/domains/FilmVideoMetaDomain.ts`): text (trim) + sourceLines + kind, theo thứ tự,
 * cyrb53. Sửa một bên thì sửa cả hai — test khoá giá trị cố định cho cùng một fixture ở cả hai repo.
 */
export const computeBeatsHash = (beats: ReadonlyArray<Pick<FilmActionBeat, 'text' | 'sourceLines' | 'kind'>> | null | undefined): string => {
  const canon = (beats || [])
    .map((b) => `${String(b?.text ?? '').trim()}|${(b?.sourceLines || []).join(',')}|${b?.kind ?? ''}`)
    .join('\n');
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < canon.length; i++) {
    const ch = canon.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16).padStart(14, '0');
};

export const isSkyPlan = (flowVersion: FilmFlowVersion | string | null | undefined): boolean => flowVersion === 'sky';

const GEN_MODEL_OPTIONS = [
  { label: 'mini — rẻ, mặc định', value: 'mini' },
  { label: 'pro — đắt ×2, cảnh hành động', value: 'pro' },
  { label: 'fast', value: 'fast' },
  { label: '2.5 — chất lượng cao, tới 30 ảnh tham chiếu', value: '2.5' },
] as const;

/** Model có thể chọn tay: plan sky đã chốt engine thì không cho chọn chéo họ model. */
export const genModelOptionsFor = (
  flowVersion: FilmFlowVersion | string | null | undefined,
  videoEngine: FilmVideoEngine | string | null | undefined,
) => {
  if (isSkyPlan(flowVersion) && videoEngine === 'SEEDANCE_25') return [GEN_MODEL_OPTIONS[3]];
  if (isSkyPlan(flowVersion) && videoEngine === 'SEEDANCE_20') return GEN_MODEL_OPTIONS.slice(0, 3);
  return [...GEN_MODEL_OPTIONS];
};

/** Nhãn ⚙️ phải phản ánh model engine hiệu lực, không phải tuning cũ còn nằm trong state. */
export const effectiveTuningLabel = (
  tuning: GenTuning,
  flowVersion: FilmFlowVersion | string | null | undefined,
  videoEngine: FilmVideoEngine | string | null | undefined,
  generation?: { engine?: string; model?: string | null; resolution?: string | null } | null,
): string => {
  const saved = optInTuningOf({ generation }); // 261010 UI2: video opt-in ⇒ nhãn là cấu hình ĐÃ LƯU
  const model = saved ? (saved.model ?? '2.5') : isSkyPlan(flowVersion) && videoEngine === 'SEEDANCE_25' ? '2.5' : (tuning.model || 'mini');
  const resolution = saved ? saved.resolution : isSkyPlan(flowVersion) && videoEngine === 'SEEDANCE_25' ? '480p' : (tuning.resolution || '480p');
  return `${model} · ${resolution}${(tuning.count || 1) > 1 ? ` · ${tuning.count} bản` : ''}`;
};

/**
 * Tag cam ">15s" (E1-P2 Bước 9) cạnh tiêu đề event v2; trả `null` = không hiện tag.
 * `beatTiming.overflow` là bộ ước LEGACY so với trần 15s của lần gen hiện tại. Plan sky có bộ ước riêng (`video_meta.estimate`, hiện ở chip) nên
 * hai con số không được cùng xuất hiện trên một thẻ (S5, 20/10):
 *  - sky + SEEDANCE_25: LUÔN hiện tag, không phụ thuộc `beatTiming`; chữ KHÔNG ghi con số
 *    (số chỉ nằm ở chip), không nhắc ✂️/"tách theo nhịp" (làm theo sẽ xoá đề xuất engine, BE `clearSkyFieldsIfSkyPlan`);
 *  - sky + SEEDANCE_20: KHÔNG hiện (D4: ước 2.0 đã ≤15s; beatTiming cũ là bộ ước khác nên không được báo ngược);
 *  - plan NULL, sky + engine NULL (event đã sửa tay): hành vi cũ từng ký tự — hiện khi `beatTiming.overflow`.
 */
export const overflowTagView = (
  beatTiming: { rawS: number; overflow?: boolean; splitPoints: number[] } | null | undefined,
  flowVersion: FilmFlowVersion | string | null | undefined,
  videoEngine: FilmVideoEngine | string | null | undefined,
): { text: string; tip: string } | null => {
  if (isSkyPlan(flowVersion)) {
    if (videoEngine === 'SEEDANCE_25') {
      return {
        text: 'video 2.5 — trần 30s',
        tip: 'Video này gen bằng engine 2.5, tối đa 30 giây một lượt. Con số ước xem ở chip engine.',
      };
    }
    if (videoEngine === 'SEEDANCE_20') return null;
  }
  if (!beatTiming?.overflow) return null;
  return {
    text: `ước ${beatTiming.rawS}s > 15s — ${beatTiming.splitPoints.length ? 'tách theo nhịp' : 'không có chỗ tách'}`,
    tip: 'Giây ước theo nhịp + thoại vượt trần 15s của một lần gen — bấm ✂️ để tách theo nhịp',
  };
};

export type SkyTagTone = 'warn' | 'info' | 'error';
export interface SkyTag { key: string; text: string; tone: SkyTagTone; tip?: string }
export interface SkyPhaseView {
  order: number;
  purpose: string;
  endState: string;
  beats: Array<{ order: number; text: string }>;
  /** Câu thoại neo vào giai đoạn này (suy từ `afterActionBeatOrder` lúc đọc). */
  lines: Array<{ speaker: string; text: string; voiceOver: boolean }>;
  cutAfterAllowed: boolean;
  cutAfterReason: string;
}
export interface SkyEventView {
  /** PROPOSED = AI/code đã đề xuất; MANUAL = event của plan sky nhưng engine NULL (đã sửa cấu trúc tay — chưa có đề xuất). */
  state: 'PROPOSED' | 'MANUAL';
  engineLabel: '2.0' | '2.5' | null;
  secondsLabel: string | null;
  reason: string | null;
  tags: SkyTag[];
  phases: SkyPhaseView[];
  /** Số câu thoại chưa neo nhịp nào (không gán được vào giai đoạn). */
  unanchoredLines: number;
  stale: boolean;
}

const ENGINE_LABEL: Record<FilmVideoEngine, '2.0' | '2.5'> = { SEEDANCE_20: '2.0', SEEDANCE_25: '2.5' };
const FLAG_TAGS: Record<string, { text: string; tone: SkyTagTone; tip: string }> = {
  NEEDS_HUMAN_CUT: { text: 'Cần người cắt tay', tone: 'error', tip: 'Ước vượt trần 2.5 và không có chỗ cắt hợp lệ giữa các giai đoạn — code không tự cắt.' },
  SHORT_SOURCE_BLOCK: { text: 'Khối nguồn ngắn', tone: 'info', tip: 'Đoạn nguồn quá ngắn (thường do nguồn xen kẽ nơi) — video ngắn hơn mức tối thiểu của engine.' },
  SPLIT_CHILD: { text: 'Tách từ tình huống dài', tone: 'info', tip: 'Video này do code tách từ một tình huống dài tại ranh giới giai đoạn.' },
};

/** `video_meta` đọc từ response: đúng dạng mới dùng, sai dạng ⇒ null (reader không sập). */
export const readVideoMeta = (raw: unknown): FilmVideoMeta | null => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Partial<FilmVideoMeta>;
  return r.schemaVersion === 1 && Array.isArray(r.phases) ? (r as FilmVideoMeta) : null;
};

/** Giai đoạn đã cũ ⇔ băm nhịp hiện tại khác băm lúc chia. Thiếu băm hoặc thiếu nhịp ⇒ không kết luận (false). */
export const isPhaseStale = (meta: FilmVideoMeta | null, beats: FilmActionBeat[] | null | undefined): boolean =>
  !!meta && !!meta.beatsHash && !!beats && computeBeatsHash(beats) !== meta.beatsHash;

/** Thoại thuộc giai đoạn nào: neo 0 ⇒ giai đoạn đầu; neo k ≥ 1 ⇒ giai đoạn chứa nhịp k; neo null/lạ ⇒ không gán. */
const phaseIndexOfAnchor = (phases: FilmVideoPhase[], anchor: unknown): number => {
  if (typeof anchor !== 'number' || !Number.isInteger(anchor) || anchor < 0) return -1;
  if (anchor === 0) return phases.length ? 0 : -1;
  return phases.findIndex((p) => p.beatOrders.includes(anchor));
};

const fmtS = (x: unknown): string | null => (typeof x === 'number' && Number.isFinite(x) ? String(Math.round(x * 10) / 10) : null);

/** View cho MỘT event. Plan không phải `sky` ⇒ null (KHÔNG hiện gì mới). */
export function skyEventView(ev: Pick<FilmEvent, 'videoEngine' | 'engineReason' | 'videoMeta' | 'beatMeta' | 'dialogueLines'>, flowVersion: FilmFlowVersion | string | null | undefined): SkyEventView | null {
  if (!isSkyPlan(flowVersion)) return null;
  const meta = readVideoMeta(ev.videoMeta);
  const engine = ev.videoEngine && ENGINE_LABEL[ev.videoEngine] ? ev.videoEngine : null;
  const beats = [...(ev.beatMeta?.actionBeats || [])].sort((a, b) => a.order - b.order);
  const stale = isPhaseStale(meta, ev.beatMeta?.actionBeats);

  const tags: SkyTag[] = [];
  if (!engine) tags.push({ key: 'MANUAL', text: 'chưa có đề xuất (đã sửa tay)', tone: 'warn', tip: 'Event này vừa bị tách/gộp nên đề xuất engine + giai đoạn cũ đã bị bỏ. Tính lại đề xuất là việc của bản sau.' });
  for (const f of meta?.flags || []) {
    const t = FLAG_TAGS[f];
    if (t) tags.push({ key: f, text: t.text, tone: t.tone, tip: t.tip });
  }
  if (stale) tags.push({ key: 'STALE', text: 'giai đoạn đã cũ', tone: 'warn', tip: 'Nhịp đã bị sửa sau khi chia — các giai đoạn bên dưới có thể không còn khớp. Không tự sửa.' });

  const lines = (ev.dialogueLines || []).slice().sort((a, b) => a.orderIndex - b.orderIndex);
  const phases: SkyPhaseView[] = (meta?.phases || []).slice().sort((a, b) => a.order - b.order).map((p) => ({
    order: p.order,
    purpose: p.purpose,
    endState: p.endState,
    beats: p.beatOrders.map((o) => ({ order: o, text: beats.find((b) => b.order === o)?.text ?? `(nhịp ${o})` })),
    lines: [],
    cutAfterAllowed: p.cutAfterAllowed,
    cutAfterReason: p.cutAfterReason,
  }));
  let unanchoredLines = 0;
  for (const l of lines) {
    if (!String(l.textEn || '').trim()) continue;
    const pi = phaseIndexOfAnchor(meta?.phases ? [...meta.phases].sort((a, b) => a.order - b.order) : [], l.afterActionBeatOrder);
    if (pi < 0 || !phases[pi]) { unanchoredLines += 1; continue; }
    phases[pi].lines.push({ speaker: String(l.speakerName || ''), text: String(l.textEn || ''), voiceOver: l.dialogueMode === 'NARRATOR' });
  }

  const sec = meta && engine ? fmtS(engine === 'SEEDANCE_20' ? meta.estimate?.seconds20 : meta.estimate?.seconds25) : null;
  return {
    state: engine ? 'PROPOSED' : 'MANUAL',
    engineLabel: engine ? ENGINE_LABEL[engine] : null,
    secondsLabel: sec ? `ước ${sec}s` : null,
    reason: ev.engineReason || null,
    tags,
    phases,
    unanchoredLines,
    stale,
  };
}

/**
 * Tham số `genEventPlan` từ form "New Film Plan". Công tắc "Luồng mới" TẮT/vắng ⇒ KHÔNG có khoá `flowVersion` trong request (body y hệt trước đợt này);
 * BẬT ⇒ `flowVersion: 'sky'`. Tách ra để test không cần antd.
 */
export function buildGenEventPlanOpts(
  values: { name?: string; aestheticRef?: string; skyFlow?: boolean; genEngine?: string; genModel?: string | null; genResolution?: string | null },
  promptMode: 'TECHNICAL' | 'NATURAL',
): { name?: string; aestheticRef?: string; promptMode: 'TECHNICAL' | 'NATURAL'; flowVersion?: 'sky'; generation?: GenerationBody } {
  // 261010 UI2 (L2): `generation` chỉ đi cùng luồng mới BẬT và khi form engine hợp lệ (BE: generation ngoài sky ⇒ 400). Tắt luồng mới / không chọn ⇒ body y hệt trước.
  const gen = values.skyFlow === true && values.genEngine
    ? generationBodyOf({ engine: values.genEngine as GenerationForm['engine'], model: values.genModel, resolution: values.genResolution })
    : null;
  return {
    name: values.name?.trim() || undefined,
    aestheticRef: values.aestheticRef?.trim() || undefined,
    promptMode,
    ...(values.skyFlow === true ? { flowVersion: 'sky' as const } : {}),
    ...(gen && gen.ok ? { generation: gen.body } : {}),
  };
}
